import { supabase } from "@/integrations/supabase/client";

export interface QueuedBook {
  book_id: string;
  title: string;
  authors: string[];
  isbn13: string | null;
  isbn10: string | null;
  canonical_isbn13: string | null;
  enrichment_status: string;
  page_count: number | null;
  synopsis: string | null;
  cover_url: string | null;
  in_user_library: boolean;
  user_count?: number;
  language: string | null;
}

export async function getEnrichmentQueue(limit: number = 100, offset: number = 0): Promise<{ books: QueuedBook[]; hasMore: boolean; total: number }> {
  try {
    console.log(`[EnrichmentQueue] Fetching queue (limit: ${limit}, offset: ${offset})`);
    
    // Use optimized direct query with pagination (faster than RPC)
    const { data, error, count } = await supabase
      .from('books')
      .select(`
        id,
        title,
        authors,
        isbn13,
        isbn10,
        canonical_isbn13,
        enrichment_status,
        page_count,
        synopsis,
        cover_url,
        language
      `, { count: 'exact' })
      .or('synopsis.is.null,page_count.is.null')
      .in('enrichment_status', ['pending', 'failed'])
      .order('enrichment_status', { ascending: false }) // 'pending' before 'failed'
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('[EnrichmentQueue] Error:', error);
      throw error;
    }

    const mappedData: QueuedBook[] = (data || []).map((item: any) => ({
      book_id: item.id,
      title: item.title,
      authors: item.authors || [],
      isbn13: item.isbn13 || null,
      isbn10: item.isbn10 || null,
      canonical_isbn13: item.canonical_isbn13 || null,
      enrichment_status: item.enrichment_status || 'pending',
      page_count: item.page_count || null,
      synopsis: item.synopsis || null,
      cover_url: item.cover_url || null,
      in_user_library: false, // We'd need a join to determine this, skip for performance
      user_count: 0,
      language: item.language || null
    }));

    return {
      books: mappedData,
      hasMore: (count || 0) > offset + limit,
      total: count || 0
    };
  } catch (error) {
    console.error('[EnrichmentQueue] Failed to fetch:', error);
    throw error;
  }
}

export async function updateBookISBN(
  bookId: string, 
  isbn13: string | null, 
  isbn10: string | null
): Promise<void> {
  try {
    const updateData: any = {
      isbn13: isbn13 || null,
      isbn10: isbn10 || null,
      canonical_isbn13: isbn13 || isbn10 || null,
      enrichment_status: 'pending',
      enrichment_attempted_at: null
    };

    const { error } = await supabase
      .from('books')
      .update(updateData)
      .eq('id', bookId);

    if (error) {
      console.error('[EnrichmentQueue] Update error:', error);
      throw error;
    }

    console.log(`[EnrichmentQueue] Updated ISBN for book ${bookId}`);
  } catch (error) {
    console.error('[EnrichmentQueue] Failed to update ISBN:', error);
    throw error;
  }
}

export async function enrichSingleBook(bookId: string): Promise<any> {
  try {
    console.log(`[EnrichmentQueue] Triggering enrichment for book ${bookId}`);
    
    const { data, error } = await supabase.functions.invoke('enrich-books', {
      body: {
        action: 'enrich-books',
        bookIds: [bookId],
        batchSize: 1,
        prioritizeIsbndb: true,
        languageFilter: 'en',
        extractSeries: true
      }
    });

    if (error) {
      throw error;
    }

    console.log(`[EnrichmentQueue] Enrichment result:`, data);
    return data;
  } catch (error) {
    console.error(`[EnrichmentQueue] Failed to enrich book ${bookId}:`, error);
    throw error;
  }
}
